import { useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { flushSync } from 'react-dom';
import { TranslationService } from '../../services/translation.service';
import ValidationSummary from '../Shared/ValidationSummary';
import TextFieldFormGroup, { type ITextFieldFormGroupState } from '../Shared/TextFieldFormGroup';
import ValidationInfo from '../../validation/validation-info';
import type IValidatable from '../Shared/validatable.interface';
import SubmitSuccessful from '../Shared/SubmitSuccessful';
import SubmitFailed from '../Shared/SubmitFailed';
import ContactInformationLookup, { type IContactInformationLookupState } from '../Shared/ContactInformationLookup';
import { ContactInformationLookupField } from '../Shared/ContactInformationLookup/contact-information-lookup-field.enum';
import Consent, { type IConsentState } from '../Shared/Consent';
import HoneyPot, { type IHoneyPot } from '../Shared/HoneyPot';
import { ValidatableComponent, ValidationHelper } from '../../helpers/validation.helper';
import type { IServerValidationError } from '../Shared/validatable.interface';
import type IRefundApplication from './models/refund-application.model';
import { LanguageService } from '../../services/language.service';
import { AnalyticsService } from '../../services/analytics-service';
import { Status } from '../../hooks/fetch.hook';
import { emptyContactInformation } from '../../models/contact-information.model';
import { useTexts } from '../../hooks/texts.hook';
import AntiforgeryService from '../../services/anti-forgery.service';
import { PersonalIdentityNumberFeature } from '../Shared/PersonalIdentityNumberLookup/personal-identity-lookup-feature.enum';
import { IServerSideRendered } from '../Shared/Base/server-side-rendered';
import PaymentMethodSelector, { IPaymentMethodSelectorState } from '../Shared/PaymentMethodSelector';
import { PaymentMethod } from '../../models/payment-method.enum';
import { PaymentMethodSelectorField } from '../Shared/PaymentMethodSelector/payment-method-selector-field.enum';
import { SanitizerService } from '../../services/sanitizer.service';

export interface IRefundTexts {
  textBody: string;
  commentsHeading: string;
  submitApplication: string;
  completedText: string;
  noPersonalIdentityNumberText: string;
  consentText: string;
  integrityPolicyInfo: string;
  failedSubmissionText: string;
  dismissFailedSubmissionText: string;
}

export type IRefundSettings = object;

type IRefundProps = IServerSideRendered<IRefundTexts, IRefundSettings>;

function Refund({ texts }: IRefundProps) {
  // === HOOKS ===
  const { t } = useTexts();

  // === STATE ===
  const [descriptionState, setDescriptionState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });

  const [contactInformationLookupState, setContactInformationLookupState] = useState<IContactInformationLookupState>({
    value: emptyContactInformation,
    validationInfo: new ValidationInfo(),
  });
  const [consentState, setConsentState] = useState<IConsentState>({ value: false, validationInfo: new ValidationInfo() });
  const [submitStatus, setSubmitStatus] = useState(Status.notperformed);
  const [submitErrorCode, setSubmitErrorCode] = useState<number | undefined>(undefined);
  const [paymentMethodSelectorState, setPaymentMethodSelectorState] = useState<IPaymentMethodSelectorState>({
    value: { paymentMethod: PaymentMethod.ToGo, travelCardNumber: '', toGoTicketNumber: '', otherTicketType: '' },
    validationInfo: new ValidationInfo(),
  });

  // === REFS ===
  const descriptionRef = useRef<IValidatable>(null!);
  const contactInformationLookupRef = useRef<IValidatable>(null!);
  const consentRef = useRef<IValidatable>(null!);
  const honeyPotRef = useRef<IHoneyPot>(null!);
  const paymentMethodSelectorRef = useRef<IValidatable>(null!);

  // === HELPER FUNCTIONS ===
  function getValidatableComponents(): ValidatableComponent[] {
    return [
      { component: paymentMethodSelectorRef.current, validationInfo: paymentMethodSelectorState.validationInfo, setState: setPaymentMethodSelectorState },
      { component: contactInformationLookupRef.current, validationInfo: contactInformationLookupState.validationInfo, setState: setContactInformationLookupState },
      { component: consentRef.current, validationInfo: consentState.validationInfo, setState: setConsentState },
      { component: descriptionRef.current, validationInfo: descriptionState.validationInfo, setState: setDescriptionState },
    ];
  }

  function handleServerValidationErrors(errors: IServerValidationError[]): void {
    const invalidValidationInfos = ValidationHelper.handleServerValidationErrors(errors, getValidatableComponents());
    ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
      AnalyticsService.addEvent(AnalyticsService.Refund, AnalyticsService.ErrorAction, e);
    });
  }

  // === VARIABLES ===
  const validationSummaryErrors: string[] = ValidationHelper.getValidationSummaryErrors(getValidatableComponents());

  // === EVENT HANDLERS ===
  async function handleSubmitClick() {
    let isFormValid: boolean | undefined;
    let invalidValidationInfos: ValidationInfo[];
    let submitResult: { status: Status; errorCode: number | undefined } | undefined;
    await flushSync(async () => {
      if (submitStatus === Status.loading) return;

      setSubmitStatus(Status.notperformed);
      setSubmitErrorCode(undefined);

      if (!honeyPotRef.current.isValid()) return;

      [isFormValid, invalidValidationInfos] = ValidationHelper.checkValidity(getValidatableComponents());
      if (!isFormValid) {
        ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
          AnalyticsService.addEvent(AnalyticsService.Refund, AnalyticsService.ErrorAction, e);
        });
        return;
      }

      setSubmitStatus(Status.loading);
      const application: IRefundApplication = {
        refundComments: descriptionState.value,
        personalIdentityNumber: contactInformationLookupState.value.personalIdentityNumber,
        phoneNumber: contactInformationLookupState.value.phoneNumber,
        email: contactInformationLookupState.value.email,
        isConsentGiven: consentState.value,
        formCreatedDateTime: DateTime.now(),
        paymentMethodType: paymentMethodSelectorState.value.paymentMethod,
        paymentMethodToGoTicketNumber: paymentMethodSelectorState.value.toGoTicketNumber,
        paymentMethodTravelCardNumber: paymentMethodSelectorState.value.travelCardNumber,
      };

      const formData = new FormData();
      formData.append('refundInfo', JSON.stringify(application));

      try {
        const response = await fetch(`/api/refund?language=${LanguageService.getCurrentLanguage()}`, {
          method: 'POST',
          body: formData,
          headers: { 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
        });
        if (response.ok) {
          setSubmitStatus(Status.success);
          submitResult = { status: Status.success, errorCode: undefined };
          AnalyticsService.addEvent(AnalyticsService.Refund, 'Skickat återköp', '');
        } else {
          if (response.status === 400) {
            const responseBody = await response.json();
            if (!responseBody.errors) throw new Error('Missing errors');
            handleServerValidationErrors(responseBody.errors);
          }
          setSubmitStatus(Status.failed);
          setSubmitErrorCode(response.status);
          submitResult = { status: Status.failed, errorCode: response.status };
          AnalyticsService.addEvent(AnalyticsService.Refund, 'Skickat återköp - misslyckat', '');
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        setSubmitStatus(Status.failed);
        submitResult = { status: Status.failed, errorCode: (error as { status: number }).status };
      }
    });
    setTimeout(() => {
      document.getElementById('refund-form')?.scrollIntoView(true);
      const shouldFocusValidationSummary = isFormValid === false || submitResult?.errorCode === 400;
      document.getElementById(shouldFocusValidationSummary ? 'refund-form-validation-summary' : 'refund-form-submit-result')?.focus();
    }, 100);
  }

  return (
    <div id="refund-form" className="refund-form">
      <div hidden={!(submitStatus !== Status.success && (submitStatus !== Status.failed || submitErrorCode === 400))}>
        {texts.textBody && <div className="mb-4 mb-md-5" dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(texts.textBody) }}></div>}
        <div className="mb-3 border-sm p-sm-3">
          {TranslationService.get('common.formsrequiredfieldsmessage') && <p className="mb-3">{TranslationService.get('common.formsrequiredfieldsmessage')}</p>}
          <ValidationSummary id="refund-form-validation-summary" validationSummaryErrors={validationSummaryErrors} />
          <TextFieldFormGroup
            ref={descriptionRef}
            renderAsTextArea={true}
            maxLength={2000}
            labelText={texts.commentsHeading}
            required={true}
            inputId="refund-comments"
            serverValidationName="refundComments"
            autocomplete="off"
            state={descriptionState}
            onChange={(value) => {
              setDescriptionState((ps) => ({ ...ps, value }));
            }}
            onKeyUp={() => {
              setDescriptionState((ps) => ({ ...ps, validationInfo: new ValidationInfo() }));
            }}
          />
          <PaymentMethodSelector
            ref={paymentMethodSelectorRef}
            state={paymentMethodSelectorState}
            heading={t('Jag har min periodbiljett', 'I have my season ticket')}
            availablePaymentMethods={[PaymentMethod.ToGo, PaymentMethod.TravelCard]}
            radioLabelTranslation={(paymentMethod) => {
              switch (paymentMethod) {
                case PaymentMethod.ToGo:
                  return t('I appen Västtrafik To Go', 'In the Västtrafik To Go app');
                case PaymentMethod.TravelCard:
                  return t('På ett Västtrafikkort', 'On a Västtrafik card');
                default:
                  return '';
              }
            }}
            onChange={(paymentMethod) => setPaymentMethodSelectorState((ps) => ({ ...ps, value: paymentMethod }))}
            onKeyUp={(e) => {
              if (e === PaymentMethodSelectorField.paymentMethod) {
                setPaymentMethodSelectorState((ps) => ({
                  ...ps,
                  validationInfo: new ValidationInfo(),
                }));
              } else if (e === PaymentMethodSelectorField.toGoTicketNumber) {
                setPaymentMethodSelectorState((ps) => ({
                  ...ps,
                  validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, { ...ps.validationInfo.children, toGoTicketNumber: new ValidationInfo() }),
                }));
              } else if (e === PaymentMethodSelectorField.travelCardNumber) {
                setPaymentMethodSelectorState((ps) => ({
                  ...ps,
                  validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, { ...ps.validationInfo.children, travelCardNumber: new ValidationInfo() }),
                }));
              }
            }}
          />
          <hr className="my-4" />
          <ContactInformationLookup
            ref={contactInformationLookupRef}
            state={contactInformationLookupState}
            feature={PersonalIdentityNumberFeature.Refund}
            noPersonalIdentityNumberText={texts.noPersonalIdentityNumberText}
            onChange={(value) => {
              setContactInformationLookupState((ps) => ({ ...ps, value }));
            }}
            onKeyUp={(e) => {
              if (e === ContactInformationLookupField.personalIdentityNumber) {
                setContactInformationLookupState((ps) => ({
                  ...ps,
                  validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, { ...ps.validationInfo.children, personalIdentityNumber: new ValidationInfo() }),
                }));
              } else if (e === ContactInformationLookupField.phoneNumber) {
                setContactInformationLookupState((ps) => ({
                  ...ps,
                  validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, {
                    ...ps.validationInfo.children,
                    phoneNumber: new ValidationInfo(),
                  }),
                }));
              } else if (e === ContactInformationLookupField.email) {
                setContactInformationLookupState((ps) => ({
                  ...ps,
                  validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, {
                    ...ps.validationInfo.children,
                    email: new ValidationInfo(),
                  }),
                }));
              }
            }}
          />
          {contactInformationLookupState.value.valid && (
            <div>
              <hr className="my-4" />
              <div className="compensation-type">
                <div className="h4 mb-2">{t('Så här får du ersättning', 'How to receive your compensation')}</div>
                <p className="mb-0">
                  {t('Du får ersättningen som en värdekod i appen Västtrafik To Go. Värdekoden skickas via e-post.', 'You will receive a voucher code in the Västtrafik To Go app, via email.')}
                </p>
              </div>
              <hr className="my-4" />
              <Consent
                id="refund-consent"
                ref={consentRef}
                consentText={texts.consentText}
                integrityPolicyInfo={texts.integrityPolicyInfo}
                termsAndConditions={t('Villkor', 'Terms and conditions')}
                state={consentState}
                onChange={(value) => {
                  setConsentState({ value, validationInfo: new ValidationInfo() });
                }}
              />
              <hr className="my-4" />
              <HoneyPot ref={honeyPotRef} />
              <div className="d-grid">
                <button type="submit" className={`btn btn-primary ${submitStatus === Status.loading ? 'btn-spinner' : ''}`} onClick={handleSubmitClick}>
                  <span hidden={submitStatus === Status.loading}>{texts.submitApplication}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <div id="refund-form-submit-result" role="alert" aria-live="polite" tabIndex={-1}>
        {submitStatus === Status.failed && submitErrorCode !== 400 && (
          <>
            <SubmitFailed failedText={texts.failedSubmissionText} />
            <button
              className="btn btn-primary d-block mx-auto"
              onClick={() => {
                setSubmitStatus(Status.notperformed);
              }}
            >
              {texts.dismissFailedSubmissionText}
            </button>
          </>
        )}
        {submitStatus === Status.success && <SubmitSuccessful successText={texts.completedText} />}
      </div>
    </div>
  );
}

export default Refund;
